import React, { useMemo } from 'react';
import { Badge, Flex, Select } from 'antd';
import { Tooltip } from '../common/components/Tooltip';
import styled, { createGlobalStyle } from 'styled-components';
import {
  colorDraftActiveBg,
  colorDraftBg,
  colorDraftBorder,
  colorGray,
  colorInactiveActiveBg,
  colorInactiveBg,
  colorInactiveBorder,
  colorSuccessActiveBg,
  colorSuccessBg,
  colorSuccessBorder,
  fontSize,
  fontWeightNormal,
  fontWeightStrong,
  letterSpacing,
} from '@atlas-frontend/design-tokens';
import { DATE_FORMAT, getValidPeriodString } from '../common/utils';
import { getSortedVersions } from './utils';
import * as Icon from 'react-feather';
import type { VersionDataType } from './types';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '../configuration';

type DefaultClassifiers = {
  DATA_OBJ_GRP_VER_STATUS: SystemClassifierOption[];
};

type OptionNode = {
  label: React.ReactNode;
  value: number | string;
  className?: string;
};

type Props<TVersion = VersionDataType> = {
  versions: TVersion[];
  activeVersionId?: number | string;
  onChange?: (value: number | string | null | undefined) => void;
  disabled?: boolean;
  wrapperHeight?: string | number;
  allowClear?: boolean;
  placeholder?: string;
  getValue?: (version: TVersion) => number | string;
  renderOption?: (
    version: TVersion,
    classifiers: DefaultClassifiers,
  ) => OptionNode;
  sortVersions?: (versions: TVersion[]) => TVersion[];
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
};

const defaultRenderOption = (
  version: VersionDataType,
  { DATA_OBJ_GRP_VER_STATUS }: DefaultClassifiers,
): OptionNode => ({
  label: (
    <Tooltip title={version.description} placement="left">
      <DataObjectVersionOptionLabel
        className={version.statusCode || 'DRAFT'}
        gap="small"
      >
        <DataObjectVersionOptionBadge
          count={version.versionNumber}
          showZero
          color="black"
          size="small"
        />{' '}
        {getValidPeriodString(version.validPeriod, DATE_FORMAT)}{' '}
        {
          DATA_OBJ_GRP_VER_STATUS.find(
            (item) => item.value === version.statusCode,
          )?.label
        }
      </DataObjectVersionOptionLabel>
    </Tooltip>
  ),
  value: version.id,
  className: version.statusCode || 'DRAFT',
});

const defaultSortVersions = (versions: VersionDataType[]) =>
  getSortedVersions(versions);

export function DataObjectVersionSelect<TVersion = VersionDataType>({
  versions,
  activeVersionId,
  onChange,
  disabled,
  wrapperHeight,
  allowClear = false,
  placeholder,
  renderOption,
  sortVersions,
  size,
  style,
}: Props<TVersion>) {
  const { DATA_OBJ_GRP_VER_STATUS } = useSystemClassifierSelectOptions(
    ['DATA_OBJ_GRP_VER_STATUS'],
    'code',
  );

  const renderOptionFinal =
    (renderOption as Props<VersionDataType>['renderOption']) ??
    defaultRenderOption;
  const sortVersionsFinal =
    (sortVersions as Props<VersionDataType>['sortVersions']) ??
    defaultSortVersions;

  const versionOptions = useMemo(() => {
    const list = sortVersionsFinal(versions as unknown as VersionDataType[]);
    return list.map((v) => renderOptionFinal(v, { DATA_OBJ_GRP_VER_STATUS }));
  }, [versions, sortVersionsFinal, renderOptionFinal, DATA_OBJ_GRP_VER_STATUS]);

  return (
    <>
      <VersionSelectGlobalStyle />
      <SelectWrapper
        id="versionId"
        size={size}
        options={versionOptions}
        value={activeVersionId ?? null}
        onChange={(val) => {
          onChange?.(val as number | string | null);
        }}
        onClear={() => onChange?.(null)}
        styles={{
          popup: { root: { width: 'max-content' } },
        }}
        classNames={{ popup: { root: 'version-select-popup' } }}
        suffixIcon={<Icon.ChevronDown size={18} color={colorGray[7]} />}
        disabled={disabled}
        height={wrapperHeight}
        allowClear={allowClear}
        placeholder={placeholder}
        style={style}
      />
    </>
  );
}

interface SelectWrapperProps {
  height?: string | number;
}

const SelectWrapper = styled(Select<any, any>)<SelectWrapperProps>`
  height: ${({ height }) =>
    typeof height === 'number' ? `${height}px` : height || '24px'};
  margin: auto 0;

  .ant-select-arrow {
    inset-inline-end: 8px;
  }

  :has(.DRAFT) {
    border-color: ${colorDraftBorder} !important;
    background-color: ${colorDraftBg} !important;
  }

  :has(.ACTIVE),
  :has(.CONF_PUB),
  :has(.CONFIRMED),
  :has(.CONF_WAIT),
  :has(.CONF_PREPUB) {
    border-color: ${colorSuccessBorder} !important;
    background-color: ${colorSuccessBg} !important;
  }
  :has(.INACTIVE) {
    border-color: ${colorInactiveBorder} !important;
    background-color: ${colorInactiveBg} !important;
  }

  .ant-select-selector {
    padding-inline: 6px !important;
    width: 100%;

    .ant-select-selection-item {
      padding-inline-end: 24px;
    }

    .ant-select-selection-wrap {
      height: ${({ height }) =>
        typeof height === 'number'
          ? `${height}px`
          : height || '24px'} !important;
    }
  }
`;

const VersionSelectGlobalStyle = createGlobalStyle`
  .version-select-popup .ant-select-item.DRAFT {
    background-color: ${colorDraftBg} !important;
    &.ant-select-item-option-active {
      background-color: ${colorDraftActiveBg} !important;
    }
  }

  .version-select-popup .ant-select-item.ACTIVE,
  .version-select-popup .ant-select-item.CONF_PUB,
  .version-select-popup .ant-select-item.CONFIRMED,
  .version-select-popup .ant-select-item.CONF_WAIT,
  .version-select-popup .ant-select-item.CONF_PREPUB {
    background-color: ${colorSuccessBg} !important;
    &.ant-select-item-option-active {
      background-color: ${colorSuccessActiveBg} !important;
    }
  }

  .version-select-popup .ant-select-item.INACTIVE {
    background-color: ${colorInactiveBg} !important;
    &.ant-select-item-option-active {
      background-color: ${colorInactiveActiveBg} !important;
    }
  }
`;

export const DataObjectVersionOptionLabel = styled(Flex)`
  align-items: center;
  font-size: 11px;
  font-weight: ${fontWeightStrong};
  letter-spacing: 0.1em;
  height: 24px;
  color: ${colorGray[8]};
  background-color: inherit;

  .ant-select-dropdown & {
    font-size: ${fontSize}px;
    font-weight: ${fontWeightNormal};
    letter-spacing: ${letterSpacing}em;
  }
`;

export const DataObjectVersionOptionBadge = styled(Badge)`
  .ant-badge-count {
    font-size: 11px;
    box-shadow: none;
    border-radius: 3px;
    color: white;
    padding-inline: 2px;
  }
`;
